import { Injectable, inject } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import {
  AlignmentType,
  BorderStyle,
  Document,
  Footer,
  Header,
  ImageRun,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx'
import { saveAs } from 'file-saver'
import { TRACKING_VIEW_COLUMNS } from 'src/app/tracking/tracking.model'

const NO_BORDER = {
  top: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
}

@Injectable({ providedIn: 'root' })
export class ExportCommentsService {
  private snackBar = inject(MatSnackBar)

  private async getImageAsArrayBufferSafe(url: string): Promise<ArrayBuffer | null> {
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error('Image not found')
      return await res.arrayBuffer()
    } catch (err) {
      console.warn(`⚠️ Could not load logo: ${url}`, err)
      return null
    }
  }

  async downloadProgramComments(program: any): Promise<void> {
    try {
      // Defensive copy to prevent mutation during export
      const programSnapshot = JSON.parse(JSON.stringify(program))

      const client = programSnapshot?.clientName?.value || 'Client'
      const programName = programSnapshot?.programName?.value || 'Program'
      const inceptionDate = programSnapshot?.inceptionDate?.value || ''
      const title = `${client} – ${programName} – ${inceptionDate}`

      // Load logo safely
      const logoBuffer = await this.getImageAsArrayBufferSafe('assets/logos/corp-logo-black.png')
      //  Header (logo only, centered, with spacing)
      const headerRow = new TableRow({
        children: [
          new TableCell({
            borders: NO_BORDER,
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                spacing: { after: 400 },
                children: logoBuffer
                  ? [
                      new ImageRun({
                        data: logoBuffer,
                        transformation: {
                          width: 148,
                          height: 78,
                        },
                      }),
                    ]
                  : [],
              }),
            ],
          }),
        ],
      })

      const headerTable = new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [headerRow],
      })

      const paragraphs: Paragraph[] = []

      //  Main title (centered)
      paragraphs.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 300 },
          children: [
            new TextRun({
              text: title,
              bold: true,
              font: 'Segoe UI',
              size: 28, // ~14pt
            }),
          ],
        }),
      )

      // Filter only columns with comments
      const orderedColumns = TRACKING_VIEW_COLUMNS.filter(
        (col) => programSnapshot?.[col.id]?.comments?.length > 0,
      )

      if (orderedColumns.length === 0) {
        paragraphs.push(
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: 'No comments available for this program.',
                italics: true,
                color: '888888',
                font: 'Segoe UI',
                size: 20,
              }),
            ],
          }),
        )
      } else {
        orderedColumns.forEach((col) => {
          const colData = programSnapshot[col.id]
          const comments = colData?.comments || []

          // Section heading
          paragraphs.push(
            new Paragraph({
              spacing: { before: 200, after: 100 },
              children: [
                new TextRun({
                  text: String(col.label ?? ''),
                  bold: true,
                  font: 'Segoe UI',
                  size: 24,
                }),
              ],
            }),
          )

          // Latest comment only (sorted by date descending, take first)
          const latestComment = [...comments].sort(
            (a: any, b: any) =>
              new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
          )[0]

          if (latestComment) {
            paragraphs.push(
              new Paragraph({
                spacing: { after: 100 },
                children: [
                  new TextRun({
                    text: String(latestComment?.text ?? ''),
                    font: 'Segoe UI',
                    size: 20,
                  }),
                ],
              }),
            )
          }
        })
      }

      // Final document
      const doc = new Document({
        sections: [
          {
            headers: {
              default: new Header({ children: [headerTable] }),
            },
            footers: {
              default: new Footer({
                children: [
                  new Paragraph({
                    alignment: AlignmentType.CENTER,
                    children: [
                      new TextRun({
                        text: 'Generated on ' + new Date().toLocaleString(),
                        size: 18,
                        color: '888888',
                        font: 'Segoe UI',
                      }),
                    ],
                  }),
                ],
              }),
            },
            children: paragraphs,
          },
        ],
      })

      //  Save Word file
      const blob = await Packer.toBlob(doc)
      const safeName = programName.replace(/\s+/g, '_')
      const fileName = `Comments_${safeName}.docx`
      saveAs(blob, fileName)
    } catch (err) {
      console.error('Comment export failed:', err)
      this.snackBar.open(`Export failed for program. Please try again.`, 'Close', {
        duration: 5000,
        panelClass: 'snackbar-white-bg',
      })
    }
  }
}
